import React, { useEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { capturePng, downloadCanvas } from '../../../../utils/pngExport'
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    Drawer,
    IconButton,
    LinearProgress,
    TextField,
} from '@mui/material'
import {
    ArrowForwardRounded,
    CloseRounded,
    ExpandMoreRounded,
    ImageRounded,
} from '@mui/icons-material'
import { StudentRatingState } from '../../../../features/types'
import { ratingApiSlice } from '../../../../features/rating/ratingSlice'
import { ui, tone } from '../../../../utils/uiTokens'
import ReportSheet from '../reportPage/ReportSheet'

import { useHeadScope } from '../../../head/headScope'

interface Props {
    student: StudentRatingState | null
    open: boolean
    onClose: () => void
    monthNumber?: number
    weekNumber?: number
    totalCount?: number
}

interface DetailRow {
    title: string
    sub: string
    pct: number
    badge: string
    good: boolean
    points: number
    barColor: string
}

const badgeTone: Record<string, { bg: string; color: string; key: string }> = {
    auto: { bg: tone.info.bg, color: tone.info.text, key: 'badgeAuto' },
    mentor: { bg: tone.warn.bg, color: tone.warn.text, key: 'badgeMentor' },
    mixed: { bg: tone.neutral.bg, color: tone.neutral.text, key: 'badgeMixed' },
}

const CARDS = [
    { id: 'video', color: ui.pink, badge: 'auto', title: 'drawerVideo', desc: 'drawerVideoDesc' },
    { id: 'test', color: ui.pink, badge: 'auto', title: 'drawerTest', desc: 'drawerTestDesc' },
    { id: 'conspect', color: ui.pink, badge: 'mentor', title: 'drawerConspect', desc: 'drawerConspectDesc' },
    { id: 'zachet', color: ui.pink, badge: 'mentor', title: 'drawerZachet', desc: 'drawerZachetDesc' },
    { id: 'practice', color: ui.pink, badge: 'mixed', title: 'drawerPractice', desc: 'drawerPracticeDesc' },
]

// Лист с полным разбором бывает в тысячи пикселей высотой. При scale 2 канва
// упирается в лимит площади браузера и выходит пустой, а кодирование такого PNG
// вешает вкладку, поэтому масштаб считаем от размера листа.
const CANVAS_PIXEL_BUDGET = 16_000_000

const StudentDetailDrawer: React.FC<Props> = ({
    student,
    open,
    onClose,
    monthNumber,
    weekNumber,
    totalCount,
}) => {
    const { formatMessage } = useIntl()
    const { readOnly, basePath } = useHeadScope()
    const navigate = useNavigate()
    const sheetRef = useRef<HTMLDivElement>(null)

    const { data: report, isFetching } = ratingApiSlice.useGetWeekReportQuery(
        {
            userSubjectEnrollId: student?.userSubjectEnrollId ?? 0,
            monthNumber,
            weekNumber,
        },
        { skip: !student },
    )
    const [saveWeekScore, { isLoading: isSaving }] = ratingApiSlice.useSaveWeekScoreMutation()

    const [comment, setComment] = useState('')
    const [expanded, setExpanded] = useState<string[]>([])
    const [zachetOpen, setZachetOpen] = useState(false)
    const [isRendering, setIsRendering] = useState(false)
    const [zachetValue, setZachetValue] = useState<number | null>(null)

    useEffect(() => {
        setComment(report?.weekScore?.mentorComment || '')
        setZachetValue(report?.weekScore?.zachetPoint ?? null)
    }, [report])

    const toggleExpand = (id: string) =>
        setExpanded((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))

    // Строки детализации в едином виде: заголовок, подпись, мини-прогресс,
    // бейдж и баллы справа - как в макете
    const buildRows = (cardId: string): DetailRow[] => {
        if (cardId === 'test') {
            return (report?.testRows || []).map((r) => ({
                title: r.testTitle,
                sub: `${r.point} / ${r.maxPoint} ${formatMessage({ id: 'correctShort' })}`,
                pct: r.percent,
                badge: `${r.percent}%`,
                good: r.percent >= 70,
                points: r.ratingPoint,
                barColor: ui.pink,
            }))
        }
        if (cardId === 'conspect') {
            return (report?.videoRows || []).map((r) => ({
                title: r.videoTitle || r.topicTitle,
                sub: r.conspectPoint
                    ? formatMessage({ id: 'conspectChecked' })
                    : formatMessage({ id: 'notMarked' }),
                pct: Math.min(100, (r.conspectPoint / 5) * 100),
                badge: r.conspectPoint ? '✓' : '',
                good: !!r.conspectPoint,
                points: r.conspectPoint,
                barColor: tone.ok.text,
            }))
        }
        return (report?.videoRows || []).map((r) => ({
            title: r.videoTitle || r.topicTitle,
            sub: `${formatMessage({ id: 'watched' })} ${r.watchedPercent}%`,
            pct: r.watchedPercent,
            badge: r.watchedPercent >= 85 ? '85%+ ✓' : `${r.watchedPercent}%`,
            good: r.watchedPercent >= 85,
            points: r.point,
            barColor: r.watchedPercent >= 90 ? tone.ok.text : tone.warn.text,
        }))
    }

    if (!student) return null

    const summary = report?.summary
    const score = report?.weekScore

    const basePayload = {
        userSubjectEnrollId: student.userSubjectEnrollId,
        monthNumber: monthNumber || 0,
        weekNumber: weekNumber || 0,
        zachetPoint: score?.zachetPoint ?? null,
        practiceStatus: score?.practiceStatus || '',
        practice2Status: score?.practice2Status || '',
        zachetComment: score?.zachetComment || '',
        mentorComment: score?.mentorComment || '',
    }

    const values = summary
        ? {
              video: { value: summary.videoPoint, max: summary.videoTotal * 20, rows: report?.videoRows?.length || 0 },
              test: { value: summary.testPoint, max: summary.testTotal * 20, rows: report?.testRows?.length || 0 },
              conspect: { value: summary.conspectPoint, max: summary.conspectMax, rows: report?.videoRows?.length || 0 },
              zachet: { value: summary.zachetPoint, max: 20, rows: 0 },
              practice: { value: summary.practicePoint, max: 40, rows: 0 },
          }
        : null

    const initials = `${student.firstName?.[0] || ''}${student.lastName?.[0] || ''}`.toUpperCase()

    const reportUrl = () => {
        const p = new URLSearchParams()
        if (monthNumber) p.set('monthNumber', String(monthNumber))
        if (weekNumber) p.set('weekNumber', String(weekNumber))
        return `${basePath}/rating/${student.userSubjectEnrollId}?${p.toString()}`
    }

    const saveComment = async () => {
        try {
            await saveWeekScore({ ...basePayload, mentorComment: comment }).unwrap()
            toast.success(formatMessage({ id: 'saved' }))
        } catch {}
    }

    const saveZachet = async () => {
        try {
            await saveWeekScore({ ...basePayload, zachetPoint: zachetValue }).unwrap()
            toast.success(formatMessage({ id: 'saved' }))
            setZachetOpen(false)
        } catch {}
    }

    // PNG рисуем со скрытого полного отчёта (тот же макет, что и PDF-страница),
    // не переходя на неё. Лист монтируется только на время рендера: иначе шторка
    // держит второй экземпляр всех строк отчёта просто так.
    const downloadPng = async () => {
        if (!report || isRendering) return
        flushSync(() => setIsRendering(true))
        try {
            const el = sheetRef.current
            if (!el) return
            const area = el.offsetWidth * el.offsetHeight
            // ниже 1 текст отчёта становится нечитаемым, поэтому только вниз до 1
            const scale =
                area > 0
                    ? Math.max(1, Math.min(2, Math.sqrt(CANVAS_PIXEL_BUDGET / area)))
                    : 2
            const canvas = await capturePng(el, scale)
            await downloadCanvas(canvas, `${student.firstName}_${student.lastName}.png`.replace(/\s+/g, '_'))
        } catch {
            toast.error(formatMessage({ id: 'pngDownloadFail' }))
        } finally {
            setIsRendering(false)
        }
    }

    return (
        <>
        <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: styles.paper }}>
            {isFetching && <LinearProgress />}

            <Box sx={styles.body}>
                {/* header */}
                <Box sx={styles.header}>
                    <Box sx={styles.avatar}>{initials}</Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={styles.name}>
                            {student.firstName} {student.lastName}
                        </Box>
                        <Box sx={styles.sub}>{student.groupTitle}</Box>
                    </Box>
                    <IconButton onClick={onClose} data-html2canvas-ignore>
                        <CloseRounded />
                    </IconButton>
                </Box>

                {/* stat cards */}
                <Box sx={styles.stats}>
                    <Box sx={styles.statCard}>
                        <Box sx={styles.statLabel}>{formatMessage({ id: 'totalPoint' })}</Box>
                        <Box sx={styles.statValue}>
                            {student.totalPoint} <small>/ {student.maxPoint}</small>
                        </Box>
                    </Box>
                    <Box sx={styles.statCard}>
                        <Box sx={styles.statLabel}>{formatMessage({ id: 'place' })}</Box>
                        <Box sx={styles.statValue}>
                            {report?.rank || '-'} <small>/ {totalCount || '-'}</small>
                        </Box>
                    </Box>
                </Box>

                {/* PDF report + PNG */}
                <Box sx={{ display: 'flex', gap: 1.2 }} data-html2canvas-ignore>
                    <Button
                        fullWidth
                        onClick={() => navigate(reportUrl())}
                        sx={styles.pdfBtn}
                        endIcon={<ArrowForwardRounded />}
                    >
                        <b style={{ marginRight: 8, color: ui.pink }}>PDF</b>
                        {formatMessage({ id: 'reportPdf' })}
                    </Button>
                    <Button
                        onClick={downloadPng}
                        disabled={isRendering || isFetching || !report}
                        sx={styles.pngBtn}
                        startIcon={
                            isRendering ? <CircularProgress size={16} /> : <ImageRounded />
                        }
                    >
                        PNG
                    </Button>
                </Box>

                {/* mentor comment */}
                <Box sx={styles.block}>
                    <Box sx={styles.blockTitle}>{formatMessage({ id: 'mentorFeedback' })}</Box>
                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        value={comment}
                        disabled={readOnly}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder={formatMessage({ id: 'mentorCommentPlaceholder' })}
                        sx={{ mt: 1 }}
                    />
                    {!readOnly && <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                        <Box sx={styles.hint}>{formatMessage({ id: 'commentInPdf' })}</Box>
                        <Button variant="contained" disabled={isSaving} onClick={saveComment} data-html2canvas-ignore>
                            {formatMessage({ id: 'save' })}
                        </Button>
                    </Box>}
                </Box>

                {/* breakdown */}
                <Box sx={styles.sectionLabel}>{formatMessage({ id: 'scoreBreakdown' })}</Box>
                {values &&
                    CARDS.map((card) => {
                        const v = (values as any)[card.id]
                        const pct = v.max ? Math.min(100, Math.round((v.value * 100) / v.max)) : 0
                        const tone = badgeTone[card.badge]
                        const isOpen = expanded.includes(card.id)
                        return (
                            <Box key={card.id} sx={styles.card}>
                                <Box sx={styles.cardTop}>
                                    <Box sx={{ ...styles.dot, background: card.color }} />
                                    <Box sx={styles.cardTitle}>{formatMessage({ id: card.title })}</Box>
                                    <Box sx={{ ...styles.badge, background: tone.bg, color: tone.color }}>
                                        {formatMessage({ id: tone.key })}
                                    </Box>
                                    <Box sx={styles.cardPoints}>
                                        +{v.value} / {v.max}
                                    </Box>
                                    {v.rows > 0 && (
                                        <IconButton
                                            size="small"
                                            data-html2canvas-ignore
                                            onClick={() => toggleExpand(card.id)}
                                        >
                                            <ExpandMoreRounded
                                                sx={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: '.15s' }}
                                            />
                                        </IconButton>
                                    )}
                                </Box>
                                <Box sx={styles.cardDesc}>{formatMessage({ id: card.desc })}</Box>
                                <Box sx={styles.track}>
                                    <Box sx={{ ...styles.fill, width: `${pct}%`, background: card.color }} />
                                </Box>
                                {v.rows > 0 && (
                                    <Box
                                        sx={styles.more}
                                        data-html2canvas-ignore
                                        onClick={() => toggleExpand(card.id)}
                                    >
                                        {isOpen ? (
                                            formatMessage({ id: 'hideDetails' })
                                        ) : (
                                            <>
                                                {formatMessage({ id: 'moreDetails' })} · {v.rows}{' '}
                                                {formatMessage({ id: 'notesWord' })}
                                            </>
                                        )}
                                    </Box>
                                )}
                                {isOpen && (
                                    <Box sx={styles.rows}>
                                        {buildRows(card.id).map((row, i) => (
                                            <Box key={i} sx={styles.detailRow}>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Box sx={styles.detailTitle}>{row.title}</Box>
                                                    <Box sx={styles.detailSub}>{row.sub}</Box>
                                                    <Box sx={styles.miniTrack}>
                                                        <Box
                                                            sx={{
                                                                ...styles.miniFill,
                                                                width: `${row.pct}%`,
                                                                background: row.barColor,
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>
                                                <Box sx={styles.detailRight}>
                                                    {row.badge && (
                                                        <Box
                                                            sx={{
                                                                ...styles.rowBadge,
                                                                ...(row.good ? styles.rowBadgeGood : {}),
                                                            }}
                                                        >
                                                            {row.badge}
                                                        </Box>
                                                    )}
                                                    <Box sx={styles.detailPoints}>+{row.points}</Box>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        )
                    })}
            </Box>

            {/* sticky actions */}
            <Box sx={styles.actions} data-html2canvas-ignore>
                <Button fullWidth onClick={() => navigate(reportUrl())} sx={styles.conspectBtn}>
                    {formatMessage({ id: 'conspectChecked' })}
                </Button>
                {!readOnly && (
                    <Button fullWidth variant="outlined" onClick={() => setZachetOpen(true)} sx={styles.zachetBtn}>
                        {formatMessage({ id: 'setZachet' })}
                    </Button>
                )}
            </Box>

            {/* zachet dialog */}
            <Dialog open={zachetOpen} onClose={() => setZachetOpen(false)}>
                <Box sx={{ p: 3, minWidth: 340 }}>
                    <Box sx={styles.blockTitle}>{formatMessage({ id: 'setZachet' })}</Box>
                    <Box sx={{ ...styles.statValue, my: 1.5, color: ui.pink }}>
                        {zachetValue ?? 0} <small style={{ color: ui.muted }}>/ 20</small>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                        {[0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20].map((n) => (
                            <Box
                                key={n}
                                onClick={() => setZachetValue(n)}
                                sx={{ ...styles.gradeChip, ...(zachetValue === n ? styles.gradeChipActive : {}) }}
                            >
                                {n}
                            </Box>
                        ))}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, mt: 2.5 }}>
                        <Button fullWidth onClick={() => setZachetOpen(false)}>
                            {formatMessage({ id: 'cancel' })}
                        </Button>
                        <Button fullWidth variant="contained" disabled={isSaving} onClick={saveZachet}>
                            {formatMessage({ id: 'save' })}
                        </Button>
                    </Box>
                </Box>
            </Dialog>
        </Drawer>

        {/* Скрытый полный отчёт для рендера PNG (не показывается пользователю) */}
        {isRendering && report && (
            <Box ref={sheetRef} id="png-report-sheet" sx={styles.hiddenSheet} aria-hidden>
                <ReportSheet report={report} monthNumber={monthNumber} weekNumber={weekNumber} />
            </Box>
        )}
        </>
    )
}

export default StudentDetailDrawer

const styles: Record<string, any> = {
    paper: { width: 460, maxWidth: '100vw', display: 'flex', flexDirection: 'column', background: ui.surface },
    hiddenSheet: { position: 'fixed', left: '-10000px', top: 0, width: '940px', background: ui.card, pointerEvents: 'none', zIndex: -1 },
    body: { flex: 1, overflowY: 'auto', p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.6 },
    header: { display: 'flex', alignItems: 'center', gap: 1.5 },
    avatar: {
        width: 48, height: 48, borderRadius: '50%', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 16, fontWeight: 700, color: ui.pink, background: ui.pinkSoft,
    },
    name: { fontSize: 18, fontWeight: 700, color: ui.ink },
    sub: { fontSize: 13, color: ui.muted },
    stats: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.2 },
    statCard: { background: ui.card, border: `1px solid ${ui.border}`, borderRadius: '14px', p: 1.6 },
    statLabel: { fontSize: 12, letterSpacing: '.04em', color: ui.muted, textTransform: 'uppercase' },
    statValue: { fontSize: 26, fontWeight: 800, color: ui.ink, mt: 0.5, '& small': { fontSize: 14, color: ui.muted, fontWeight: 600 } },
    pdfBtn: {
        justifyContent: 'flex-start', textTransform: 'none', fontWeight: 600, fontSize: 14,
        background: ui.card, color: ui.ink, border: `1px solid ${ui.pink}`, borderRadius: '12px', py: 1.3,
        '&:hover': { background: ui.pinkSoft },
    },
    pngBtn: {
        textTransform: 'none', fontWeight: 600, background: ui.card, color: ui.ink,
        border: `1px solid ${ui.border}`, borderRadius: '12px', px: 2,
    },
    block: { background: ui.card, border: `1px solid ${ui.border}`, borderRadius: '14px', p: 1.8 },
    blockTitle: { fontSize: 15, fontWeight: 700, color: ui.ink },
    hint: { fontSize: 12, color: ui.muted },
    sectionLabel: { fontSize: 12.5, letterSpacing: '.05em', color: ui.muted, textTransform: 'uppercase', mt: 0.6 },
    card: { background: ui.card, border: `1px solid ${ui.border}`, borderRadius: '14px', p: 1.6 },
    cardTop: { display: 'flex', alignItems: 'center', gap: 1 },
    dot: { width: 10, height: 10, borderRadius: '50%', flexShrink: 0 },
    cardTitle: { fontSize: 14.5, fontWeight: 600, color: ui.ink, flex: 1, minWidth: 0 },
    badge: { fontSize: 11, fontWeight: 700, borderRadius: '6px', px: 0.8, py: 0.2, letterSpacing: '.03em' },
    cardPoints: { fontSize: 15, fontWeight: 800, color: ui.ink, whiteSpace: 'nowrap' },
    cardDesc: { fontSize: 12.5, color: ui.muted, mt: 0.8, lineHeight: 1.45 },
    track: { height: 6, borderRadius: '999px', background: ui.track, mt: 1 },
    fill: { height: '100%', borderRadius: '999px', transition: 'width .3s' },
    more: { fontSize: 13, fontWeight: 600, color: ui.pink, mt: 1, cursor: 'pointer' },
    rows: { mt: 1.4, pt: 1.2, borderTop: `1px solid ${ui.border}`, display: 'flex', flexDirection: 'column', gap: 1.4 },
    detailRow: { display: 'flex', alignItems: 'center', gap: 1.2 },
    detailTitle: { fontSize: 13.5, fontWeight: 600, color: ui.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    detailSub: { fontSize: 12, color: ui.muted, mt: 0.2 },
    miniTrack: { height: 5, borderRadius: '999px', background: ui.track, mt: 0.7 },
    miniFill: { height: '100%', borderRadius: '999px', transition: 'width .3s' },
    detailRight: { display: 'flex', alignItems: 'center', gap: 1.2, flexShrink: 0 },
    rowBadge: { fontSize: 11, fontWeight: 700, borderRadius: '6px', px: 0.9, py: 0.3, background: tone.neutral.bg, color: ui.muted, whiteSpace: 'nowrap' },
    rowBadgeGood: { background: tone.ok.bg, color: tone.ok.text },
    detailPoints: { fontSize: 14, fontWeight: 800, color: ui.ink, minWidth: 44, textAlign: 'right' },
    actions: {
        display: 'flex', gap: 1.2, p: 2, borderTop: `1px solid ${ui.border}`, background: ui.card,
    },
    conspectBtn: { textTransform: 'none', fontWeight: 600, color: ui.pink, background: ui.pinkSoft, borderRadius: '12px', py: 1.2, '&:hover': { background: ui.pinkSoft, color: ui.pinkDark } },
    zachetBtn: { textTransform: 'none', fontWeight: 600, color: ui.pink, borderColor: ui.pink, borderRadius: '12px', py: 1.2 },
    gradeChip: { width: 40, height: 40, borderRadius: '10px', border: `1px solid ${ui.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, cursor: 'pointer', fontSize: 14 },
    gradeChipActive: { background: ui.pink, color: '#fff', borderColor: ui.pink },
}
